/**
 * VIVA HUB WhatsApp Messages — Admin inbox for Meta Cloud API webhook traffic
 */

import React, { useCallback, useEffect, useState } from 'react'
import HubLayout from '../components/hub/HubLayout'
import {
  DatatableCard,
  DatatableEmpty,
  DatatableScroll,
  DatatableTh,
  DATATABLE_CELL_CLASS,
  DATATABLE_TABLE_CLASS,
  DATATABLE_TBODY_CLASS,
  DATATABLE_THEAD_CLASS,
  useClientDatatable,
} from '../components/shared/Datatable'
import DatatablePagination from '../components/shared/DatatablePagination'
import { hubWhatsAppAPI } from '../utils/hub/api'
import { formatDbDateTime, parseDbDateTime } from '../utils/datetime'

const KIND_FILTERS = [
  { value: '', label: 'All types' },
  { value: 'consult', label: 'Consult' },
  { value: 'door_code', label: 'Door code' },
]

const REPLY_FILTERS = [
  { value: '', label: 'All replies' },
  { value: 'sent', label: 'Reply sent' },
  { value: 'failed', label: 'Reply failed' },
  { value: 'skipped', label: 'Reply skipped' },
]

function formatKind(kind) {
  if (kind === 'door_code') return 'Door code'
  if (kind === 'consult') return 'Consult'
  return kind || '—'
}

function formatReply(status) {
  if (status === 'sent') return 'Sent'
  if (status === 'failed') return 'Failed'
  if (status === 'skipped') return 'Skipped'
  return status || '—'
}

function replyBadgeClass(status) {
  if (status === 'sent') return 'bg-green-100 text-green-800'
  if (status === 'failed') return 'bg-red-100 text-red-800'
  return 'bg-gray-100 text-gray-700'
}

function MessageDetailModal({ message, onClose }) {
  useEffect(() => {
    if (!message) return undefined
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [message, onClose])

  if (!message) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whatsapp-msg-detail-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="flex max-h-[min(90vh,720px)] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div>
            <h2 id="whatsapp-msg-detail-title" className="text-lg font-semibold text-gray-900">
              WhatsApp message
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">#{message.id}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close"
          >
            <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <dl className="grid grid-cols-1 gap-x-4 gap-y-2 border-b border-gray-100 px-5 py-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-gray-500">When</dt>
            <dd className="font-medium text-gray-900">{formatDbDateTime(message.created_at)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">From</dt>
            <dd className="font-medium text-gray-900">{message.from_phone || '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Contact</dt>
            <dd className="font-medium text-gray-900">{message.contact_name || '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Type</dt>
            <dd className="font-medium text-gray-900">{formatKind(message.classification)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Reply</dt>
            <dd className="font-medium text-gray-900">{formatReply(message.reply_status)}</dd>
          </div>
          {message.lead_id ? (
            <div>
              <dt className="text-gray-500">CRM case</dt>
              <dd className="font-medium text-gray-900">
                <a className="text-green-700 hover:underline" href={`/hub/crm/${message.lead_id}`}>
                  #{message.lead_id}
                </a>
              </dd>
            </div>
          ) : null}
          <div className="sm:col-span-2">
            <dt className="text-gray-500">Message ID</dt>
            <dd className="font-mono text-xs text-gray-800 break-all">{message.wa_message_id}</dd>
          </div>
        </dl>

        <div className="overflow-y-auto px-5 py-4 space-y-4 text-sm">
          <div>
            <h3 className="font-medium text-gray-900 mb-1">Inbound</h3>
            <pre className="whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-gray-800">
              {message.body_text || '(empty)'}
            </pre>
          </div>
          {message.reply_body ? (
            <div>
              <h3 className="font-medium text-gray-900 mb-1">Auto-reply</h3>
              <pre className="whitespace-pre-wrap rounded-md bg-green-50 p-3 text-gray-800">
                {message.reply_body}
              </pre>
            </div>
          ) : null}
          {message.reply_error ? (
            <div>
              <h3 className="font-medium text-red-800 mb-1">Reply error</h3>
              <pre className="whitespace-pre-wrap rounded-md bg-red-50 p-3 text-red-800">
                {message.reply_error}
              </pre>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default function HubWhatsApp() {
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [classification, setClassification] = useState('')
  const [replyStatus, setReplyStatus] = useState('')
  const [selected, setSelected] = useState(null)

  const closeDetail = useCallback(() => setSelected(null), [])

  const table = useClientDatatable(messages, {
    pageLength: 15,
    initialSort: { key: 'created_at', dir: 'desc' },
    getSortValue: (row, key) => {
      if (key === 'created_at') return parseDbDateTime(row.created_at)
      if (key === 'from') return row.contact_name || row.from_phone || ''
      return row[key]
    },
  })

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError('')
      try {
        const params = { limit: 200 }
        if (q) params.q = q
        if (classification) params.classification = classification
        if (replyStatus) params.reply_status = replyStatus
        const result = await hubWhatsAppAPI.list(params)
        if (!cancelled) setMessages(result.messages || [])
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load WhatsApp messages')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [q, classification, replyStatus])

  return (
    <HubLayout appName="WhatsApp">
      <MessageDetailModal message={selected} onClose={closeDetail} />

      <p className="text-gray-600 mb-4">
        Inbound messages to the Meta WhatsApp Cloud API number (+1 561-560-3728), including auto-reply
        status. Admin only.
      </p>

      <form
        className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault()
          setQ(searchInput.trim())
        }}
      >
        <div className="flex-1 min-w-0">
          <label htmlFor="wa-search" className="block text-sm font-medium text-gray-700 mb-1">
            Search
          </label>
          <input
            id="wa-search"
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Phone, name, message text…"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-green-600 focus:outline-none focus:ring-1 focus:ring-green-600"
          />
        </div>
        <button
          type="submit"
          className="rounded-md bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800"
        >
          Search
        </button>
        {q ? (
          <button
            type="button"
            onClick={() => {
              setSearchInput('')
              setQ('')
            }}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Clear
          </button>
        ) : null}
      </form>

      <div className="mb-4 flex flex-wrap gap-2">
        {KIND_FILTERS.map((filter) => (
          <button
            key={`k-${filter.value}`}
            type="button"
            onClick={() => setClassification(filter.value)}
            className={`px-3 py-1.5 rounded-md text-sm ${
              classification === filter.value
                ? 'bg-green-100 text-green-800'
                : 'bg-white border border-gray-200 text-gray-700'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {REPLY_FILTERS.map((filter) => (
          <button
            key={`r-${filter.value}`}
            type="button"
            onClick={() => setReplyStatus(filter.value)}
            className={`px-3 py-1.5 rounded-md text-sm ${
              replyStatus === filter.value
                ? 'bg-green-100 text-green-800'
                : 'bg-white border border-gray-200 text-gray-700'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-gray-600">Loading messages…</p>}
      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <DatatableCard footer={<DatatablePagination {...table} itemLabel="messages" />}>
          <DatatableScroll>
            <table className={DATATABLE_TABLE_CLASS}>
              <thead className={DATATABLE_THEAD_CLASS}>
                <tr>
                  <DatatableTh sortKey="created_at" sort={table.sort} onSort={table.toggleSort}>
                    When
                  </DatatableTh>
                  <DatatableTh sortKey="from" sort={table.sort} onSort={table.toggleSort}>
                    From
                  </DatatableTh>
                  <DatatableTh sortKey="body_text" sort={table.sort} onSort={table.toggleSort}>
                    Message
                  </DatatableTh>
                  <DatatableTh
                    sortKey="classification"
                    sort={table.sort}
                    onSort={table.toggleSort}
                  >
                    Type
                  </DatatableTh>
                  <DatatableTh sortKey="reply_status" sort={table.sort} onSort={table.toggleSort}>
                    Reply
                  </DatatableTh>
                </tr>
              </thead>
              <tbody className={DATATABLE_TBODY_CLASS}>
                {table.sortedItems.length === 0 ? (
                  <DatatableEmpty
                    colSpan={5}
                    message="No WhatsApp messages yet. When Meta delivers a webhook, it will appear here."
                  />
                ) : (
                  table.rows.map((row) => (
                    <tr
                      key={row.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setSelected(row)}
                    >
                      <td className={DATATABLE_CELL_CLASS}>{formatDbDateTime(row.created_at)}</td>
                      <td className={DATATABLE_CELL_CLASS}>
                        <div className="font-medium text-gray-900">
                          {row.contact_name || row.from_phone || '—'}
                        </div>
                        {row.contact_name && row.from_phone ? (
                          <div className="text-xs text-gray-500">{row.from_phone}</div>
                        ) : null}
                      </td>
                      <td className={`${DATATABLE_CELL_CLASS} max-w-xs truncate`}>
                        {row.body_text || '(empty)'}
                      </td>
                      <td className={DATATABLE_CELL_CLASS}>{formatKind(row.classification)}</td>
                      <td className={DATATABLE_CELL_CLASS}>
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${replyBadgeClass(row.reply_status)}`}
                        >
                          {formatReply(row.reply_status)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </DatatableScroll>
        </DatatableCard>
      )}
    </HubLayout>
  )
}
